import type { Component } from 'vue'

import { createOperatorReportingCreatePrefetchGuard } from '@/views/Reports/ReportExecution/guards'

type AsyncRouteComponent = Promise<{ default: Component }>

const Reports = (): AsyncRouteComponent => import('@/views/Reports/Index.vue')
const ReportsOperatorReporting = (): AsyncRouteComponent => import('@/views/Reports/OperatorReporting/Index.vue')
const ReportsOperatorReportingHome = (): AsyncRouteComponent =>
    import('@/views/Reports/OperatorReporting/OperatorReportingHome.vue')
const ReportsOperatorReportingCreate = (): AsyncRouteComponent => import('@/views/Reports/ReportExecution/CreateReport/Index.vue')
const ReportsOperatorReportingCreatePrint = (): AsyncRouteComponent =>
    import('@/views/Reports/ReportExecution/CreatePrintReport/Index.vue')
const ReportsReportExecution = (): AsyncRouteComponent => import('@/views/Reports/ReportExecution/Index.vue')
const ReportsReportExecutionDetail = (): AsyncRouteComponent => import('@/views/Reports/ReportExecution/ActiveReport/Index.vue')
const ReportsManagerApprovals = (): AsyncRouteComponent => import('@/views/Reports/ManagerApprovals/Index.vue')
const ReportsManagerApprovalsList = (): AsyncRouteComponent => import('@/views/Reports/ManagerApprovals/ManagerApprovals.vue')
const ReportsManagerApprovalsDetailedList = (): AsyncRouteComponent =>
    import('@/views/Reports/ManagerApprovals/DetailedReports.vue')
const ReportsMyReports = (): AsyncRouteComponent => import('@/views/Reports/MyReports/Index.vue')
const ReportsMyReportsList = (): AsyncRouteComponent => import('@/views/Reports/MyReports/MyReports.vue')
const ReportsMyReportsDetailedList = (): AsyncRouteComponent => import('@/views/Reports/MyReports/DetailedReports.vue')
const ReportsPaperReporting = (): AsyncRouteComponent => import('@/views/Reports/PaperReporting/Index.vue')
const ReportsPaperReportingDetail = (): AsyncRouteComponent => import('@/views/Reports/PaperReporting/DetailedPaperReports.vue')
const ReportsPaperReportingList = (): AsyncRouteComponent => import('@/views/Reports/PaperReporting/PaperReporting.vue')
const ReportsPrioritization = (): AsyncRouteComponent => import('@/views/Reports/Prioritization/Index.vue')
const ReportsPrioritizationList = (): AsyncRouteComponent => import('@/views/Reports/Prioritization/Prioritization.vue')

export default [
    {
        path: '/reports',
        name: 'reports',
        component: Reports,
        meta: {
            title: 'reports.root.pageTitle',
            menuRendered: true,
            menuTitle: 'reports.root.menuTitle',
            icon: 'mdi-clock-outline',
            requiresAuth: true,
            permission: 'reports'
        },
        children: [
            {
                path: '/reports/reports-shift-summaries',
                name: 'reportsManagerApprovals',
                component: ReportsManagerApprovals,
                meta: {
                    title: 'reports.managerApprovals.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.managerApprovals.menuTitle',
                    requiresAuth: true,
                    permission: 'reports_review'
                },
                redirect: { name: 'reportsManagerApprovalsList' },
                children: [
                    {
                        path: '',
                        name: 'reportsManagerApprovalsList',
                        component: ReportsManagerApprovalsList,
                        meta: {
                            title: 'reports.managerApprovals.routeTitle',
                            requiresAuth: true,
                            permission: 'reports_review'
                        }
                    },
                    {
                        path: ':id',
                        name: 'reportsManagerApprovalsDetailedReports',
                        component: ReportsManagerApprovalsDetailedList,
                        meta: {
                            title: 'reports.managerApprovals.detailed.routeTitle',
                            requiresAuth: true,
                            permission: 'reports_review'
                        }
                    }
                ]
            },
            {
                path: '/reports/reporting',
                name: 'reportsOperatorReporting',
                component: ReportsOperatorReporting,
                meta: {
                    title: 'reports.operatorReporting.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.operatorReporting.menuTitle',
                    requiresAuth: true,
                    permission: 'reports'
                },
                redirect: { name: 'reportsOperatorReportingHome' },
                children: [
                    {
                        path: '',
                        name: 'reportsOperatorReportingHome',
                        component: ReportsOperatorReportingHome,
                        meta: {
                            title: 'reports.operatorReporting.routeTitle',
                            requiresAuth: true,
                            permission: 'reports'
                        }
                    },
                    {
                        path: 'create',
                        name: 'reportsOperatorReportingCreate',
                        component: ReportsOperatorReportingCreate,
                        beforeEnter: createOperatorReportingCreatePrefetchGuard(),
                        meta: {
                            title: 'reports.operatorReporting.createRouteTitle',
                            requiresAuth: true,
                            permission: 'reports_create'
                        }
                    },
                    {
                        path: 'print-create',
                        name: 'reportsOperatorReportingCreatePrint',
                        component: ReportsOperatorReportingCreatePrint,
                        beforeEnter: createOperatorReportingCreatePrefetchGuard(),
                        meta: {
                            title: 'reports.operatorReporting.createRouteTitle',
                            requiresAuth: true,
                            permission: 'reports_create'
                        }
                    }
                ]
            },
            {
                path: '/reports/report-execution',
                name: 'reportsReportExecution',
                component: ReportsReportExecution,
                meta: {
                    title: 'reports.reportExecution.detail.routeTitle',
                    requiresAuth: true,
                    permission: 'reports'
                },
                children: [
                    {
                        path: ':id',
                        name: 'reportsReportExecutionDetail',
                        component: ReportsReportExecutionDetail,
                        meta: {
                            title: 'reports.reportExecution.detail.routeTitle',
                            requiresAuth: true,
                            permission: 'reports'
                        }
                    }
                ]
            },
            {
                path: '/reports/prioritization',
                name: 'reportsPrioritization',
                component: ReportsPrioritization,
                meta: {
                    title: 'reports.prioritization.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.prioritization.menuTitle',
                    requiresAuth: true,
                    permission: 'schedule_prioritization'
                },
                redirect: { name: 'reportsPrioritizationList' },
                children: [
                    {
                        path: '/reports/prioritization',
                        name: 'reportsPrioritizationList',
                        component: ReportsPrioritizationList,
                        meta: {
                            title: 'reports.prioritization.routeTitle',
                            requiresAuth: true,
                            permission: 'schedule_prioritization'
                        }
                    }
                ]
            },
            {
                path: '/reports/paper-reporting',
                name: 'reportsPaperReporting',
                component: ReportsPaperReporting,
                meta: {
                    title: 'reports.paperReporting.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.paperReporting.menuTitle',
                    requiresAuth: true,
                    permission: 'paper_report'
                },
                redirect: { name: 'reportsPaperReportingList' },
                children: [
                    {
                        path: '',
                        name: 'reportsPaperReportingList',
                        component: ReportsPaperReportingList,
                        meta: {
                            title: 'reports.paperReporting.routeTitle',
                            requiresAuth: true,
                            permission: 'paper_report'
                        }
                    },
                    {
                        path: ':id',
                        name: 'reportsPaperReportingDetail',
                        component: ReportsPaperReportingDetail,
                        meta: {
                            title: 'reports.paperReporting.detail.routeTitle',
                            requiresAuth: true,
                            permission: 'reports'
                        }
                    }
                ]
            },
            {
                path: '/reports/user-reports-shift-summaries',
                name: 'reportsMyReports',
                component: ReportsMyReports,
                meta: {
                    title: 'reports.myReports.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.myReports.menuTitle',
                    requiresAuth: true,
                    permission: 'reports'
                },
                redirect: { name: 'reportsMyReportsList' },
                children: [
                    {
                        path: '',
                        name: 'reportsMyReportsList',
                        component: ReportsMyReportsList,
                        meta: {
                            title: 'reports.myReports.routeTitle',
                            requiresAuth: true,
                            permission: 'reports'
                        }
                    },
                    {
                        path: ':id',
                        name: 'reportsMyReportsDetailedReports',
                        component: ReportsMyReportsDetailedList,
                        meta: {
                            title: 'reports.myReports.detailed.routeTitle',
                            requiresAuth: true,
                            permission: 'reports'
                        }
                    }
                ]
            }
        ]
    }
]
